import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Shield, Send, Loader2, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AccountSecurity() {
  const { user, setUser } = useAuth();
  const [enroll, setEnroll] = useState(null); // {otpauth_uri, manual_secret}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState([]);
  const [tg, setTg] = useState(null);
  const [tgLink, setTgLink] = useState(null);

  const refreshUser = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); localStorage.setItem("hk_user", JSON.stringify(data)); } catch { /* noop */ }
  };
  const loadTg = async () => { try { const { data } = await api.get("/me/telegram"); setTg(data); } catch { setTg(null); } };
  useEffect(() => { loadTg(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enroll"); setEnroll(data); setRecovery([]); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const verifyEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { code });
      setRecovery(data.recovery_codes); setEnroll(null); setCode("");
      await refreshUser();
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); } finally { setBusy(false); }
  };
  const disable2fa = async () => {
    setBusy(true);
    try { await api.post("/auth/2fa/disable", { code }); setCode(""); setRecovery([]); await refreshUser(); toast.success("2FA disabled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/recovery-codes", { code }); setRecovery(data.recovery_codes); setCode(""); toast.success("New recovery codes generated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const linkTelegram = async () => {
    setBusy(true);
    try { const { data } = await api.post("/me/telegram/link-code"); setTgLink(data); if (data.deep_link) window.open(data.deep_link, "_blank"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const unlinkTelegram = async () => { try { await api.post("/me/telegram/unlink"); setTgLink(null); loadTg(); toast.success("Telegram unlinked"); } catch { toast.error("Failed"); } };

  // Poll link status while a link is pending
  useEffect(() => {
    if (!tgLink || tg?.linked) return;
    const id = setInterval(async () => {
      try { const { data } = await api.get("/me/telegram"); if (data.linked) { setTg(data); setTgLink(null); toast.success("Telegram linked!"); } } catch { /* noop */ }
    }, 4000);
    return () => clearInterval(id);
  }, [tgLink, tg]);

  const enabled = !!user?.totp_enabled;

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="account-security">
      {/* 2FA */}
      <div className="card-moss p-6">
        <div className="flex items-center justify-between">
          <p className="overline">Two-factor authentication</p>
          {enabled ? <ShieldCheck size={18} className="text-lime-300" /> : <Shield size={18} className="text-emerald-100/50" />}
        </div>

        {enabled && recovery.length === 0 && (
          <>
            <p className="mt-2 font-editorial text-2xl text-white">Enabled</p>
            <p className="mt-1 text-sm text-emerald-100/70">Your account is protected with an authenticator app.</p>
            <div className="mt-4 grid gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="Current 6-digit or recovery code" data-testid="twofa-code-input"
                className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-lime-400/60 focus:outline-none" />
              <div className="flex flex-wrap gap-2">
                <button onClick={regen} disabled={busy || !code} data-testid="twofa-regen" className="btn-edge-ghost text-xs">Regenerate recovery codes</button>
                <button onClick={disable2fa} disabled={busy || !code} data-testid="twofa-disable" className="btn-edge-ghost text-xs text-red-300">Disable 2FA</button>
              </div>
            </div>
          </>
        )}

        {!enabled && !enroll && recovery.length === 0 && (
          <>
            <p className="mt-2 text-sm text-emerald-100/70">Add a second layer of security using Google Authenticator, Authy, or any TOTP app.</p>
            <button onClick={startEnroll} disabled={busy} data-testid="twofa-enroll" className="btn-edge mt-4 text-sm">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />} Enable 2FA
            </button>
          </>
        )}

        {enroll && (
          <div className="mt-4">
            <p className="text-sm text-emerald-100/70">Scan this in your authenticator app, then enter the 6-digit code.</p>
            <div className="mt-3 inline-block rounded-lg bg-white p-3"><QRCodeSVG value={enroll.otpauth_uri} size={168} /></div>
            <p className="mt-3 break-all text-xs text-emerald-100/60">Manual key: <span className="font-mono-edge text-emerald-200">{enroll.manual_secret}</span></p>
            <div className="mt-3 flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} inputMode="numeric" placeholder="123456" data-testid="twofa-verify-input"
                className="flex-1 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-lime-400/60 focus:outline-none" />
              <button onClick={verifyEnroll} disabled={busy || code.length !== 6} data-testid="twofa-verify" className="btn-edge text-sm">Verify</button>
            </div>
          </div>
        )}

        {recovery.length > 0 && (
          <div className="mt-4" data-testid="twofa-recovery-codes">
            <p className="text-sm font-semibold text-lime-300">Save these recovery codes — each works once.</p>
            <pre className="mt-2 rounded-lg border border-emerald-500/15 bg-[#061510]/60 p-3 text-sm text-white">{recovery.join("\n")}</pre>
            <button onClick={() => { navigator.clipboard?.writeText(recovery.join("\n")); toast.success("Copied"); }} className="btn-edge-ghost mt-2 text-xs"><Copy size={12} /> Copy codes</button>
          </div>
        )}
      </div>

      {/* Telegram linking */}
      <div className="card-moss p-6">
        <div className="flex items-center justify-between">
          <p className="overline">Telegram account</p>
          <Send size={18} className={tg?.linked ? "text-lime-300" : "text-emerald-100/50"} />
        </div>
        {tg?.linked ? (
          <>
            <p className="mt-2 font-editorial text-2xl text-white flex items-center gap-2"><Check size={18} className="text-lime-300" /> Linked</p>
            {tg.telegram_username && <p className="mt-1 text-sm text-emerald-100/70">@{tg.telegram_username}</p>}
            <p className="mt-1 text-xs text-emerald-100/50">Used for login, password reset, and per-user signal alerts.</p>
            <button onClick={unlinkTelegram} data-testid="tg-unlink" className="btn-edge-ghost mt-4 text-xs text-red-300">Unlink</button>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-emerald-100/70">Link your Telegram to enable Telegram login, reset by Telegram, and direct signal DMs.</p>
            <button onClick={linkTelegram} disabled={busy} data-testid="tg-link" className="btn-edge mt-4 text-sm">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Link Telegram
            </button>
            {tgLink && (
              <div className="mt-3 rounded-lg border border-emerald-500/15 bg-[#061510]/60 p-3 text-sm text-emerald-100/80">
                {tgLink.deep_link ? (
                  <>Tap <a href={tgLink.deep_link} target="_blank" rel="noreferrer" className="text-lime-300 underline">Open the bot</a> and press Start. Waiting for confirmation…</>
                ) : (
                  <>Open Telegram, message the bot and send <span className="font-mono-edge text-lime-300">/start link_{tgLink.code}</span></>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
